import { useContext, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useGetPlayerEl from 'chameleon/packages/blue-book-player/hooks/useGetPlayerEl';
import useListenToRenderedQuestions from 'chameleon/packages/blue-book-player/hooks/useListenToRenderedQuestions';
import { BluebookContext } from 'chameleon/packages/blue-book-player/state/BluebookContext';
import {
  addPropsToNodes,
  createContainerInElement,
  getItemByRef,
  getQuestionByReference,
  isQuestionContentNotSupported,
  renderContentTabs,
} from 'chameleon/packages/blue-book-player/utils';
import { SELECTORS } from 'chameleon/packages/blue-book-player/constants/selectors';
import { createSharedFeaturesTree } from 'chameleon/packages/blue-book-player/utility/sharedPassage';
import { isFRQ } from 'chameleon/packages/blue-book-player/utility/progressHelpers';
import { runSetItemIsLoaded } from 'chameleon/packages/blue-book-player/InnerContainer/utils';

const ContentTabsInjector = ({ learnosityApp, learnosityItemsRef }) => {
  const { state: playerState } = useContext(BluebookContext);
  const playerEl = useGetPlayerEl();
  const loadedQuestions = useRef([]);
  const isFirstItemLoading = useRef(true);
  const [nodes, setNodes] = useState([]);
  const { questions } = playerState;

  const doInject = (reference) => {
    if (!loadedQuestions.current[reference]) {
      const question = getQuestionByReference(reference, questions);

      const questionEl = getItemByRef(playerEl, reference);
      if (isQuestionContentNotSupported(questionEl)) {
        runSetItemIsLoaded(questionEl, isFirstItemLoading);
        return;
      }

      const hasSharedPassage = questionEl?.querySelector(
        SELECTORS.SHARED_PASSAGE_CONTAINER,
      );

      const lrnItems = learnosityApp?.getItems();
      const sharedFeaturesTree = createSharedFeaturesTree(lrnItems);
      const isFrqQuestion = isFRQ(question);

      if (hasSharedPassage || isFrqQuestion) {
        loadedQuestions.current[reference] = true;
        createContainerInElement(questionEl, ['inline']).then((element) => {
          const node = renderContentTabs(element, {
            questionEl,
            reference,
            sharedFeaturesTree,
            questionNr: learnosityItemsRef.current.indexOf(reference) + 1,
            isFrqQuestion,
          });
          setNodes((currentNodes) => [...currentNodes, node]);
        });
      }

      runSetItemIsLoaded(questionEl, isFirstItemLoading);
    }
  };

  useListenToRenderedQuestions(playerState, learnosityApp, doInject);

  return addPropsToNodes(nodes, {});
};

ContentTabsInjector.propTypes = {
  learnosityApp: PropTypes.object,
};

ContentTabsInjector.defaultProps = {
  learnosityApp: null,
};

export default ContentTabsInjector;
